import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import titleShape from "../../../assets/images/shape/login_straight_underline.svg";
import usersService from "../../../services/UsersService";
import InputCom from "../../Helpers/Inputs/InputCom";
import Otp from "../Otp";
import AuthLayout from "../AuthLayout";

import { useDispatch } from "react-redux";
import { updateUserInfo } from "../../../store/userInfo";

// Same key convention as myfit-app's AsyncStorage-backed remembered email
// (REMEMBERED_EMAIL_STORAGE_KEY in WelcomeScreen.tsx) — this is
// localStorage's equivalent, only read/written here.
const REMEMBERED_EMAIL_STORAGE_KEY = "myfit.rememberedEmail";

// Passwordless login — same two steps as myfit-app: enter your email, get a
// 6-digit code, enter the code. See UsersService.js's sendLoginCode()/
// verifyLoginCode(). This is the only way into the app (there is no
// separate sign-up flow — myfit-api has no passwords).
export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userApi = new usersService();

  const [step, setStep] = useState("email"); // "email" | "code"
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [email, setMail] = useState("");
  const handleEmail = (e) => {
    setMail(e.target.value);
  };

  // A saved email means the box was checked last time — pre-fill both.
  const [rememberEmail, setRememberEmail] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBERED_EMAIL_STORAGE_KEY);
    if (saved) {
      setMail(saved);
      setRememberEmail(true);
    }
  }, []);

  // Six single-digit boxes, same component/shape SignUp's own OTP step
  // uses (auto-advances focus to the next box on keyup).
  const emptyVerificationCode = {
    value_one: "",
    value_two: "",
    value_three: "",
    value_four: "",
    value_five: "",
    value_six: "",
  };
  const [verificationCode, setVerificationCode] = useState(emptyVerificationCode);
  const handleVerificationInput = ({ target: { name, value } }) => {
    setVerificationCode((prev) => ({ ...prev, [name]: value }));
  };
  const code = Object.values(verificationCode).join("");

  // Shown inline the same way myfit-app's Verify screen does — myfit-api
  // only includes this when MYFIT_RETURN_CODE=1 (local dev), so there's no
  // real email provider wired up yet.
  const [devCode, setDevCode] = useState("");

  const sendCode = async () => {
    setErrorMessage("");
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setErrorMessage("Enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await userApi.sendLoginCode(trimmedEmail);
      setDevCode(res.data.dev_code || "");
      setVerificationCode(emptyVerificationCode); // clear any stale digits (also covers Resend)

      // Save (or clear) at the point they actually submit, not on every
      // keystroke or checkbox click — matches what "remember" should mean.
      if (rememberEmail) {
        localStorage.setItem(REMEMBERED_EMAIL_STORAGE_KEY, trimmedEmail);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_STORAGE_KEY);
      }
      setStep("code");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error || "Could not send a code, please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setErrorMessage("");
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setErrorMessage("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const res = await userApi.verifyLoginCode(email.trim().toLowerCase(), trimmedCode);
      const data = res.data;

      // Mapped onto the same localStorage/profile shape the rest of the
      // app (AuthRoute, Settings, etc.) already reads — AuthRoute in
      // particular requires `profile.firstname` (not `first_name`) to
      // consider the session valid.
      const profile = {
        firstname: data.first_name || "",
        lastname: data.last_name || "",
        profile_complete: data.profile_complete,
      };
      localStorage.setItem("email", data.email);
      localStorage.setItem("session_token", data.token);
      localStorage.setItem("member_uuid", data.uid);
      localStorage.setItem("profile", JSON.stringify(profile));
      dispatch(updateUserInfo(profile));

      // Same branch as myfit-app's VerifyScreen: a brand-new account (no
      // name on file yet) goes to CompleteProfile first, not straight in.
      if (data.profile_complete) {
        navigate("/", { replace: true });
      } else {
        navigate("/complete-profile", { replace: true });
      }
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error || "Incorrect code, please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (step === "email") {
      sendCode();
    } else {
      verifyCode();
    }
  };

  return (
    <>
      <AuthLayout slogan="Welcome to myFit">
        <div className="content-wrapper w-full  flex justify-center items-center xl:bg-white dark:bg-dark-white   2xl:w-[828px] xl:w-[500px] 2xl:h-[818px] xl:h-[600px] rounded-xl 2xl:px-[164px] xl:px-[56px] sm:px-7 px-5 ">
          <div className="w-full">
            {step === "email" ? (
              <div className="title-area flex flex-col justify-center items-center relative text-center mb-7">
                <h1 className="text-5xl font-bold leading-[74px] text-dark-gray dark:text-white">
                  myFit
                </h1>
                <div className="shape mb-[10px]">
                  <img src={titleShape} alt="shape" />
                </div>
              </div>
            ) : (
              <div className="title-area flex flex-col justify-center items-center relative text-center mb-7">
                <h1 className="text-4xl font-bold leading-[54px] text-dark-gray dark:text-white">
                  Enter your code
                </h1>
                <p className="text-base text-thin-light-gray font-normal mt-2">
                  We sent a 6-digit code to {email}
                </p>
              </div>
            )}
            <form className="input-area" onSubmit={onSubmit}>
              {step === "email" ? (
                <>
                  <div className="input-item mb-2.5">
                    <InputCom
                      value={email}
                      inputHandler={handleEmail}
                      placeholder="you@example.com"
                      label="Email"
                      name="email"
                      type="email"
                      iconName="message"
                    />
                  </div>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={rememberEmail}
                    onClick={() => setRememberEmail((prev) => !prev)}
                    className="flex items-center gap-2 mb-4"
                  >
                    <span
                      className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                        rememberEmail
                          ? "bg-purple border-purple"
                          : "border-light-purple dark:border-[#5356fb29]"
                      }`}
                    >
                      {rememberEmail ? (
                        <span className="text-white text-xs leading-none">&#10003;</span>
                      ) : null}
                    </span>
                    <span className="text-sm text-dark-gray dark:text-white font-normal">
                      Remember email
                    </span>
                  </button>
                  <p className="text-sm text-thin-light-gray font-normal mb-7">
                    We&apos;ll email you a 6-digit code to confirm it&apos;s you.
                  </p>
                </>
              ) : (
                <div className="input-item mb-2.5">
                  <Otp handleChange={handleVerificationInput} value={verificationCode} />
                  {devCode ? (
                    <p className="text-sm text-purple font-normal text-center mb-5 -mt-4">
                      Dev code: {devCode}
                    </p>
                  ) : null}
                </div>
              )}

              <div className="signin-area mb-3.5">
                <div className="flex justify-center">
                  <button
                    type="submit"
                    className={`btn-login  rounded-[50px] mb-6 text-xl text-white font-bold flex justify-center bg-purple items-center ${
                      loading ? "active" : ""
                    }`}
                  >
                    {loading ? (
                      <div className="signup btn-loader"></div>
                    ) : (
                      <span>{step === "email" ? "Continue" : "Verify"}</span>
                    )}
                  </button>
                </div>
                {errorMessage && (
                  <p className="text-center text-red-700 pb-4">{errorMessage}</p>
                )}
                {step === "code" && (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={sendCode}
                      disabled={loading}
                      className="text-base text-purple"
                    >
                      Resend code
                    </button>
                  </div>
                )}
              </div>
            </form>

            {/* Same Home/About/Terms/Contact + copyright links as
                myfit-www's own footer (Footer/Main.js), pointed out at
                the marketing site rather than repeating those pages
                here — this app has no About/Terms content of its own. */}
            <div className="text-center mt-10">
              <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 text-sm text-thin-light-gray">
                <a href={process.env.REACT_APP_WWWSITE}>Home</a>
                <a href={`${process.env.REACT_APP_WWWSITE}/about`}>About</a>
                <a href={`${process.env.REACT_APP_WWWSITE}/terms`}>Terms</a>
                <a href={`${process.env.REACT_APP_WWWSITE}/contact`}>Contact Us</a>
              </div>
              <p className="text-sm text-thin-light-gray mt-3">
                &copy; {new Date().getFullYear()} - myFit
              </p>
            </div>
          </div>
        </div>
      </AuthLayout>
    </>
  );
}
